import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SquadList.css';
import CommonModal from '../../services/CommonModal';
import { customDateFormatShort } from '../../config/utils';
import { FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import projectService from '../../services/projectService';
import squadService from '../../services/squadService';
import { toast } from 'react-toastify';

const SquadsList = () => {
  const [Squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showResourceSquadsModal, setShowResourceSquadsModal] = useState(false);
  const [resourceAllocations, setResourceAllocations] = useState([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState(null);
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  const [resourceContext, setResourceContext] = useState({
    projectId: null,
    squadId: null,
  });
  const [showStatus, setShowStatus] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    status: [],
    project_id: '',
    name: '',
    code: '',
  });
  const statusDropdownRef = useRef(null);
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  console.log('Squads selectedSquad:', selectedSquad);
  const confirmDelete = async () => {
    if (!selectedSquad) return;
    try {
      setDeleteSubmitting(true);
      const response = await squadService.updatesquads(selectedSquad.id, {
        ...selectedSquad,
        status: 'Inactive',
      });
      toast.success(response?.message || 'Squad marked inactive');
      setSquads((prev) =>
        prev.map((squad) =>
          squad.id === selectedSquad.id
            ? { ...squad, status: 'Inactive' }
            : squad
        )
      );
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to update squad'
      );
      console.error('Error updating squad:', err);
    } finally {
      setDeleteSubmitting(false);
      setShowDeleteModal(false);
      setSelectedSquad(null);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchSquads();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatus(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchSquads = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await squadService.getAllsquads();
      setSquads(data.squads || []);
    } catch (err) {
      setError('Failed to fetch squads');
      console.error('Failed to fetch squads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (e) => {
    setFilters((prev) => ({ ...prev, project_id: e.target.value }));
  };

  const handleNameFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, name: e.target.value }));
  };

  const handleCodeFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, code: e.target.value }));
  };

  const filteredSquads = useMemo(() => {
    return Squads.filter((squad) => {
      const statusMatch =
        !filters.status.length || filters.status.includes(squad.status);

      const projectMatch =
        !filters.project_id ||
        String(squad.project?.id) === String(filters.project_id);

      const nameMatch =
        !filters.name ||
        squad.name?.toLowerCase().includes(filters.name.toLowerCase());

      const codeMatch =
        !filters.code ||
        squad.squad_code?.toLowerCase().includes(filters.code.toLowerCase());

      return statusMatch && projectMatch && nameMatch && codeMatch;
    });
  }, [Squads, filters]);

  const handleResourceClick = (projectId, squadId) => {
    if (!projectId) return;
    setShowResourceModal(true);
    setResourceContext({ projectId, squadId });
    setResourceError(null);
  };

  const handleResourceSubmit = () => {
    setShowResourceModal(false);
    setResourceContext({ projectId: null, squadId: null });
  };

  const handleProjectInputChange = (allocationId, value) => {
    setResourceAllocations((prev) =>
      prev.map((allocation) =>
        allocation.id === allocationId
          ? { ...allocation, available_days: value }
          : allocation
      )
    );
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Active: 'badge-success',
      Inactive: 'badge-secondary',
    };
    return statusMap[status] || 'badge-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return customDateFormatShort(dateString);
  };

  const handleSquadClick = (id, isEdit) => {
    navigate(`/app/Squads/${id}/edit`);
  };

  const handleDownloadSquadList = () => {
    if (!filteredSquads || filteredSquads.length === 0) {
      toast.error('No squads to download');
      return;
    }

    const headers = [
      'Squad Code', 'Squad Name', 'Project', 'Status',
      'Created At', 'Updated At'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = filteredSquads.map((squad) => [
      squad.squad_code,
      squad.name,
      squad.project ? `${squad.project.name} (${squad.project.code})` : 'N/A',
      squad.status,
      formatDate(squad.createdAt),
      formatDate(squad.updatedAt)
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `squad-list-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Squad list downloaded');
  };

  return (
    <div className="squad-list-container">
      <div className="squad-list-header">
        <h2 style={{ fontSize: 'xx-large', fontWeight: 'bold' }}>Squads</h2>
        <Link to="/app/squads/new" className="btn btn-primary">
          <i className="fas fa-plus"></i> New Squads
        </Link>
        <div className='ms-4'>
          <button
            onClick={handleDownloadSquadList}
            className="px-4 py-3 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download squad list"
          >
            <FaFileExcel />
          </button>
        </div>
      </div>

      <div
        className="flex flex-wrap gap-3 mb-4"
        style={{ marginBottom: '1rem', gap: '1.7rem' }}
      >
        <div className="relative" style={{ maxWidth: 500, width: '100%' }}>
          <select
            name="project_id"
            value={filters.project_id}
            onChange={handleProjectChange}
            onFocus={() => setIsProjectOpen(true)}
            onBlur={() => setIsProjectOpen(false)}
            className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>

          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {isProjectOpen ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        <input
          type="text"
          name="squad_name"
          value={filters.name}
          onChange={handleNameFilterChange}
          placeholder="Squad Name"
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          style={{ maxWidth: 500, width: '100%' }}
        />

        <input
          type="text"
          name="squad_code"
          value={filters.code}
          onChange={handleCodeFilterChange}
          placeholder="Squad Code"
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          style={{ maxWidth: 500, width: '100%' }}
        />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Squad"
        message="Are you sure you want to mark this squad as inactive?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSquad(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText={deleteSubmitting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
      />

      <div className="squad-stats">
        <div className="stat-card">
          <h3>{Squads.length}</h3>
          <p>Total Squads</p>
        </div>
        <div className="stat-card">
          <h3>{Squads.filter((squad) => squad.status === 'Active').length}</h3>
          <p>Active</p>
        </div>
        <div className="stat-card">
          <h3>{Squads.filter((squad) => squad.status === 'Inactive').length}</h3>
          <p>Inactive</p>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading Squads...</div>
      ) : (
        <div className="table-responsive">
          <table className="table squad-table">
            <thead>
              <tr>
                <th>Squad Code</th>
                <th>Squad Name</th>
                <th>Project</th>
                <th>Resource</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSquads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Squads found. <Link to="/app/squads/new">Create one</Link>
                  </td>
                </tr>
              ) : (
                filteredSquads.map((squad) => (
                  <tr key={squad.id}>
                    <td>{squad.squad_code}</td>
                    <td>
                      <span style={{ color: '#007bff' }} >
                        {squad.name}
                      </span>
                    </td>
                    <td>
                      {squad.project ? (
                        <Link to={`/app/projects/${squad.project.id}`}>
                          {squad.project.name} ({squad.project.code})
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedSquad(squad);
                          setShowResourceSquadsModal(true);
                        }}
                      >
                        {squad.squad_count}
                      </Link>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(squad.status)}`}>
                        {squad.status}
                      </span>
                    </td>
                    <td>{formatDate(squad.createdAt)}</td>
                    <td>{formatDate(squad.updatedAt)}</td>
                    <td>
                      <div className="flex gap-3">
                        <Link
                          to={`/app/Squads/${squad.id}/edit`}
                          className="btn btn-sm btn-outline-primary custom-pbtn"
                          title="Edit"
                        >
                          <i className="fas fa-edit custom-fas-icon"></i>
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedSquad(squad);
                            setShowDeleteModal(true);
                          }}
                          className="btn btn-sm btn-outline-danger custom-dbtn"
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showResourceSquadsModal && selectedSquad && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowResourceSquadsModal(false)}
        >
          <div
            className="w-[50%] overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4"
              style={{ background: "#1F2937" }}
            >
              <h2 className="text-lg font-semibold text-white">
                Resource
              </h2>

              <button
                onClick={() => setShowResourceSquadsModal(false)}
                className="text-3xl leading-none text-white transition hover:text-gray-200"
              >
                &times;
              </button>
            </div>

            {/* Content */}
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {selectedSquad.resources?.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  No resources found.
                </div>
              ) : (
                <table className="min-w-full border border-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Name
                      </th>

                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Role
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">

                    {selectedSquad.resources?.map((resource, index) => (
                      <tr
                        key={`${resource.user_id}-${index}`}
                        className="transition hover:bg-gray-50"
                      >
 

                        {/* User */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">

                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 font-semibold text-white">
                              {resource.user?.first_name?.[0]}
                              {resource.user?.last_name?.[0]}
                            </div>

                            <div>
                              <div className="font-medium text-gray-900">
                                {resource.user?.first_name}{" "}
                                {resource.user?.last_name}
                              </div>
                            </div>

                          </div>
                        </td>

                        {/* Role */}
                        <td className="px-4 py-3">
                          <span className="rounded-md bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                            {resource.role || "N/A"}
                          </span>
                        </td>

                      </tr>
                    ))}

                  </tbody>
                </table>
              )}
            </div>            
          </div>
        </div>
      )}
    </div>
  );
};

export default SquadsList;